import apiClient from '../../api/apiClient';
import { API_ENDPOINTS } from '../../api/endPoints';
import { Article, ArticleFilter, ArticleUpdateData } from '../../types/article.types';

export const getArticles = async (): Promise<Article[]> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.LIST);
  return response.data;
};

export const getFilteredArticles = async (filters: ArticleFilter): Promise<Article[]> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.FILTER, {
    params: filters,
  });
  return response.data;
};
export interface DeleteFilteredArticlesResponse {
  message: string;
  durationSeconds: number;
  articlesDeleted: number;
}

export const deleteFilteredArticles = async (filters: ArticleFilter): Promise<DeleteFilteredArticlesResponse> => {
  const response = await apiClient.delete(API_ENDPOINTS.PUBLISHER.ARTICLES.DELETE_BY_FILTER, {
    params: filters,
  });
  return response.data;
};

export const getArticleById = async (id: string): Promise<Article> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.DETAIL(id));
  return response.data;
};

export const getArticlesByPublisher = async (publisherId: string): Promise<Article[]> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.BY_PUBLISHER(publisherId));
  return response.data;
};

export const getArticlesByWebsite = async (websiteId: string): Promise<Article[]> => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.BY_WEBSITE(websiteId));
  return response.data;
};

export const updateExistingArticle = async (id: string, data: ArticleUpdateData): Promise<Article> => {
  const response = await apiClient.patch(API_ENDPOINTS.PUBLISHER.ARTICLES.UPDATE(id), data);
  return response.data;
};

export const deleteExistingArticle = async (id: string): Promise<void> => {
  await apiClient.delete(API_ENDPOINTS.PUBLISHER.ARTICLES.DELETE(id));
};

export const createArticle = async (data: Partial<Article>): Promise<Article> => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.CREATE, data);
  return response.data;
};

export const generateWeatherArticle = async (websiteId: string): Promise<Article> => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_WEATHER, { websiteId });
  return response.data;
};

export const generateArticleFromUrl = async (websiteId: string, url: string): Promise<Article> => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_FROM_URL, { 
    websiteId, 
    url 
  });
  return response.data;
};

export interface PublisherPdfImportResponse {
  websiteId: string;
  fileName: string;
  extractedCount: number;
  createdCount: number;
  duplicateCount: number;
  errorCount: number;
  createdIds: string[];
  duplicateHeadlines: string[];
  errors: string[];
}

export const generateArticlesFromPdf = async (
  websiteId: string,
  file: File,
  publishDate: string
): Promise<PublisherPdfImportResponse> => {
  const formData = new FormData();
  formData.append('websiteId', websiteId);
  formData.append('file', file);
  formData.append('publishDate', publishDate);

  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_FROM_PDF, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data as PublisherPdfImportResponse;
};

export const generateArticlesFromMultiSource = async (websiteId: string, publisherPK: string, isEmergency = false): Promise<Article[]> => {
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_MULTISOURCE, { websiteId, publisherPK, isEmergency });
  return response.data;
};

// NEW: Import or preview articles directly from an RSS feed
export interface ImportFromRssOptions {
  maxItems?: number;
  onlyNew?: boolean; // skip existing articles based on URL hash or guid
  dryRun?: boolean;  // when true, do not persist; return a preview of what would be imported
}

export interface ImportFromRssResponse {
  message?: string;
  count?: number;
  durationSeconds?: number;
  websiteId?: string;
  // Server may return a list of articles or article previews in different fields
  articles?: any[];
  importedArticles?: any[];
  previews?: any[];
  // Modeled after actual server response example
  feed?: {
    title?: string;
    link?: string;
    itemsCount?: number;
  };
  createdCount?: number;
  existingCount?: number;
  errorCount?: number;
  createdIds?: string[];
  existingIds?: string[];
  previewLinks?: string[];
  previewAuthors?: string[];
  errors?: Array<string | { message?: string; [key: string]: any }>;
  [key: string]: any;
}

export const importArticlesFromRss = async (
  websiteId: string,
  feedUrl: string,
  options?: ImportFromRssOptions
): Promise<ImportFromRssResponse> => {
  const payload: any = { websiteId, feedUrl };
  if (options) {
    if (typeof options.maxItems === 'number') payload.maxItems = options.maxItems;
    if (typeof options.onlyNew === 'boolean') payload.onlyNew = options.onlyNew;
    if (typeof options.dryRun === 'boolean') payload.dryRun = options.dryRun;
  }
  const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ARTICLES.AUTO_FROM_RSS, payload);
  return response.data as ImportFromRssResponse;
};
